import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  SessionSummary,
  useCopySearchMutation,
  useListSessionsQuery,
  useRenameSessionMutation,
} from "store/api/generatedApi";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState } from "react";
import { RenameSessionModal } from "./RenameSessionModal";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import { DeleteSessionModal } from "./DeleteSessionModal";
import { useNotifyStatus } from "contexts/status";
import React from "react";

interface Props {
  session: SessionSummary;
}

export const SessionOptionsButton = ({ session }: Props) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const { refetch } = useListSessionsQuery({ tenantName: tenant });
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [renameSession] = useRenameSessionMutation();
  const [copySearch] = useCopySearchMutation();

  const notifyStatus = useNotifyStatus();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickRename = () => {
    setRenameModalOpen(true);
    handleClose();
  };

  const handleClickDelete = () => {
    setDeleteModalOpen(true);
    handleClose();
  };

  const handleClickCopy = async () => {
    setCopyLoading(true);
    handleClose();
    await copySearch({
      tenantName: tenant,
      sessionId: session.session_id,
      copySearch: { name: `${session.name} (${t("copyNoun")})` },
    })
      .unwrap()
      .then(async () => {
        await refetch();
        notifyStatus({ type: "success", text: t("searchCopied") });
      })
      .catch(() => {
        notifyStatus({ type: "error", text: t("errorCopyingSearch") });
      })
      .finally(() => {
        setCopyLoading(false);
      });
  };

  const handleRename = React.useCallback(async (name: string) => {
    await renameSession({
      tenantName: tenant,
      sessionId: session.session_id,
      renameSession: { name },
    }).unwrap();
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only; renameSession, tenant, session.session_id are stable for the lifetime of this modal
  }, []);

  return (
    <>
      <IconButton
        aria-label={t("actions")}
        onClick={handleClick}
        loading={copyLoading}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleClickRename}>
          <ListItemIcon>
            <EditIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="button">{t("rename")}</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleClickDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="button">{t("delete")}</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => void handleClickCopy()}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="button">{t("makeACopy")}</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {renameModalOpen ? (
        <RenameSessionModal
          open={renameModalOpen}
          close={() => {
            setRenameModalOpen(false);
          }}
          originalName={session.name}
          prefillName={session.name}
          rename={handleRename}
          refetch={refetch}
        />
      ) : null}

      {deleteModalOpen ? (
        <DeleteSessionModal
          open={deleteModalOpen}
          close={() => {
            setDeleteModalOpen(false);
          }}
          sessionId={session.session_id}
          refetch={refetch}
        />
      ) : null}
    </>
  );
};
